import { useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Input, Button, Tooltip, lucideIcons, Spinner } from '@round/creatorbase-ui';
import { getIdPathFromUrl, getTiktokIDFromIdPath, isTiktokMusicUrl } from './helpers';
import { useTiktokAudios } from 'Modules/Plans/Audios/hooks/queries/useTiktokAudios';
import styles from './TiktokAudioUrlFilter.module.css';
import cn from 'classnames';

const { AlertTriangle, Music, Plus, X } = lucideIcons;

type Props = {
    value: string[];
    onChange: (value: string[]) => void;
};

export function TiktokAudioUrlFilter({ value, onChange }: Props) {
    const [audioInput, setAudioInput] = useState('');
    const [audioError, setAudioError] = useState('');

    const tiktokAudioIds = value.map(getTiktokIDFromIdPath);
    const {
        data: audios,
        isLoading,
        isFetching,
        isSuccess,
    } = useTiktokAudios(
        {
            tiktok_ids: tiktokAudioIds.join(','),
            page_size: tiktokAudioIds.length,
        },
        { enabled: tiktokAudioIds.length > 0, placeholderData: keepPreviousData }
    );

    const isDuplicate = (optionValue: string) => value.includes(optionValue);

    const addAudioUrl = () => {
        const trimmed = audioInput.trim();
        if (!trimmed) {
            return;
        }
        if (!isTiktokMusicUrl(trimmed)) {
            setAudioError('Please enter a valid TikTok music URL');
            return;
        }
        const idPath = getIdPathFromUrl(trimmed);
        if (isDuplicate(idPath)) {
            setAudioError('This URL has already been added');
            return;
        }
        onChange([...value, idPath]);
        setAudioInput('');
        setAudioError('');
    };

    const removeAudioUrl = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const hasItems = value.length > 0;

    return (
        <div className={styles.container}>
            <div className={styles.inputRow}>
                <Input
                    type="url"
                    placeholder="Paste audio URL..."
                    className={styles.input}
                    value={audioInput}
                    onChange={(e) => {
                        setAudioInput(e.target.value);
                        setAudioError('');
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addAudioUrl();
                        }
                    }}
                />
                <Button
                    variant="outline"
                    size="icon"
                    className={styles.addButton}
                    onClick={addAudioUrl}
                    disabled={!audioInput.trim()}
                >
                    <Plus className={styles.icon} />
                </Button>
            </div>
            {audioError && <p className={styles.errorText}>{audioError}</p>}
            {hasItems && (
                <div className={styles.list} role="list">
                    {value.map((idPath, i) => {
                        const audio = audios?.results?.find((a) => a.tiktok_id === getTiktokIDFromIdPath(idPath));
                        const isAudioLoading = isLoading || (isFetching && !audio);
                        const notFound = isSuccess && !audio;

                        const title = audio?.title ?? idPath;
                        const authorName = audio?.author_name;
                        return (
                            <div key={`${i}-${idPath}`} className={styles.item}>
                                {isAudioLoading ? (
                                    <Spinner className={styles.icon} aria-label="Loading" />
                                ) : notFound ? (
                                    <Tooltip>
                                        <Tooltip.Trigger asChild>
                                            <AlertTriangle className={cn(styles.icon, styles.warning)} aria-hidden />
                                        </Tooltip.Trigger>
                                        <Tooltip.Content side="top">No data was found for this audio</Tooltip.Content>
                                    </Tooltip>
                                ) : (
                                    <Music className={styles.icon} />
                                )}
                                <div className={styles.itemLabels}>
                                    <span className={styles.itemLabel} title={title}>
                                        {title}
                                    </span>
                                    <span>{' - '}</span>
                                    {!!authorName && <span className={styles.itemAuthor}>{authorName}</span>}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={styles.removeButton}
                                    onClick={() => removeAudioUrl(i)}
                                >
                                    <X />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
